
if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    init() {
      this.selectors = {
        error: ".m-product-form-message",
        variantIdInput: '[name="id"]',
        pickerFields: ["[data-picker-field]"],
        optionNodes: [".m-product-option--node"],
        productSku: "[data-variant-sku]",
        productAvailability: "[data-availability]",
        shareUrlInput: "[data-share-url]",
        stockCountdown: ".prod__stock-countdown",
        productWrapper: ".m-main-product--wrapper"
      };

      this.productWrapper = this.closest(
        this.selectors.productWrapper
      );

      if (!this.productWrapper) return;

      this.productForm = this.productWrapper.querySelector(
        "product-form"
      );

      this.domNodes = queryDomNodes(
        this.selectors,
        this.productWrapper
      );

      const products = window._themeProducts || {};
      const {
        productId,
        sectionId,
        productUrl,
        hasOnlyDefaultVariant,
        showFeaturedMedia
      } = this.dataset;

      this.productId = productId;
      this.sectionId = sectionId;
      this.productUrl = productUrl;
      this.hasOnlyDefaultVariant =
        hasOnlyDefaultVariant === "true";
      this.showFeaturedMedia =
        showFeaturedMedia === "true";

      this.variantData = this.getVariantData();
      this.productData = Object.assign(
        this.getProductJson() || {},
        products[productId]
      );

      if (!this.productData.variants) return;

      this.handleQueryString();
      this.handleVariantGroupImages();
      this.selectInitialVariant();
      this.setupEventListeners();
      this.initOptionSwatches();
    }

    handleQueryString() {
      const hasVariant = new URLSearchParams(
        window.location.search
      ).has("variant");

      this.selectedVariantDefault =
        this.dataset.disableSelectedVariantDefault === "true" &&
        this.productData &&
        this.productData.variants.length > 1 &&
        !hasVariant;

      if (this.selectedVariantDefault) {
        this.disableSelectedVariantDefault();
      }
    }

    handleVariantGroupImages() {
      this.enableVariantGroupImages =
        this.dataset.enableVariantGroupImages === "true";

      if (this.enableVariantGroupImages) {
        this.variantGroupImages = this.getVariantGroupImageData();
      }
    }

    selectInitialVariant() {
      this.selectedVariant = this.productWrapper.querySelector(
        this.selectors.variantIdInput
      );

      const id = Number(this.selectedVariant?.value);

      this.currentVariant = this.productData.variants.find(
        variant => variant.id === id
      );

      this.productData.current_variant_id =
        this.currentVariant?.id;

      this.productData.initialVariant = this.currentVariant;

      if (this.currentVariant) {
        this.getDataImageVariant(this.currentVariant.id);
        this.hideSoldOutAndUnavailableOptions();
        this.updateButton(!this.currentVariant.available);
      }
    }

    setupEventListeners() {
      const setupGallery = () => this.getMediaGallery();

      setupGallery();

      document.addEventListener(
        "matchMobile",
        setupGallery
      );

      document.addEventListener(
        "unmatchMobile",
        setupGallery
      );

      this.addEventListener(
        "change",
        () => this.onVariantChange()
      );
    }

    async getMediaGallery() {
      await this.waitForMediaGallery();
      this.setupMediaGallery();
    }

    waitForMediaGallery() {
      return new Promise(resolve => {
        const check = () => {
          this.mediaGallery = this.productWrapper.querySelector(
            "media-gallery"
          );

          this.mediaGalleryMobile =
            this.productWrapper.querySelector(
              "media-gallery-mobile"
            );

          if (this.mediaGallery || this.mediaGalleryMobile) {
            resolve();
          } else {
            setTimeout(check, 100);
          }
        };

        check();
      });
    }

    setupMediaGallery() {
      const mobile = MinimogTheme.config.mqlMobile;

      const gallery =
        (mobile && this.mediaGalleryMobile) ||
        this.mediaGallery;

      if (!gallery) return;

      this.media = gallery;

      if (mobile || gallery.mediaMode === "slider") {
        this.slides = gallery.slider?.slides;
        this.slidesNav = gallery.navSlider?.slides;
      } else {
        this.mediaItems = gallery.querySelectorAll(
          ".m-product-media--item"
        );
      }

      this.layout = mobile ? undefined : gallery.layout;

      if (
        !this.showFeaturedMedia ||
        (
          this.enableVariantGroupImages &&
          this.variantGroupImages?.enable
        )
      ) {
        this.updateMedia();
      }
    }

    disableSelectedVariantDefault() {
      this.querySelectorAll('input[type="radio"]').forEach(
        input => {
          input.checked = false;
        }
      );

      this.querySelectorAll(
        '[data-picker-field="radio"]'
      ).forEach(field => {
        field.setAttribute("data-selected-value", "");
      });

      this.querySelectorAll(
        '[data-picker-field="select"]'
      ).forEach(field => {
        const select = field.querySelector("select");

        if (select) select.value = "";

        field.setAttribute("data-selected-value", "");
      });

      this.querySelectorAll(
        ".option-label--selected"
      ).forEach(label => {
        label.textContent = "";
      });

      const input = this.productForm?.querySelector(
        '[name="id"]'
      );

      if (input && !this.hasOnlyDefaultVariant) {
        input.value = "";
      }
    }

    setProductAsUnavailable() {
      this.updateButton(true);
      this.setUnavailable();
    }

    updateProductDetailsForVariant() {
      if (!this.currentVariant) return;

      this.getDataImageVariant(this.currentVariant.id);
      this.updateMedia();
      this.updateBrowserHistory();
      this.updateVariantInput();
      this.updateProductMeta();
      this.updatePrice();

      const soldOut = !this.currentVariant.available;
      const label = soldOut
        ? window.MinimogStrings.soldOut
        : "";

      this.updateButton(soldOut, label);
      this.hideSoldOutAndUnavailableOptions();
    }

    onVariantChange() {
      this.getSelectedOptions();
      this.getSelectedVariant();
      this.updatePickupAvailability();
      this.removeErrorMessage();

      if (this.currentVariant) {
        this.updateProductDetailsForVariant();

        window.MinimogEvents.emit(
          `${this.productId}__VARIANT_CHANGE`,
          this.currentVariant,
          this
        );
      } else {
        this.setProductAsUnavailable();
      }
    }

    getDataImageVariant(id) {
      if (
        !this.variantGroupImages ||
        !this.variantGroupImages.enable
      ) {
        return;
      }

      const mapping = this.variantGroupImages.mapping.find(
        item => Number(item.id) === id
      );

      if (mapping) {
        this.currentVariantMedia = mapping.media;
      }
    }

    getProductJson() {
      try {
        const element = this.productWrapper.querySelector(
          '#productData[type="application/json"]'
        );

        if (!element) {
          throw new Error("Product data not found");
        }

        return JSON.parse(element.textContent);
      } catch (error) {
        console.error("Product JSON error:", error);
        return {};
      }
    }

    getSelectedVariant() {
      let variant;
      let options = [...this.options];

      for (
        let index = 0;
        index < this.options.length &&
        !variant &&
        options.length > 0;
        index++
      ) {
        variant = getVariantFromOptionArray(
          this.productData,
          options
        );

        if (!variant) options.pop();
      }

      if (variant) {
        this.options = [...variant.options];
        this.updateSelectedOptions();
      }

      this.currentVariant = variant;
    }

    getSelectedOptions() {
      const fields = Array.from(
        this.querySelectorAll("[data-picker-field]")
      );

      this.options = fields.map(field => {
        if (field.dataset.pickerField === "radio") {
          return field.querySelector("input:checked")?.value;
        }

        const select = field.querySelector("select");
        return select?.value;
      }).filter(value => value !== undefined);
    }

    updateSelectedOptions() {
      this.domNodes.pickerFields.forEach((field, index) => {
        const value = this.options[index];

        if (
          !value ||
          field.dataset.selectedValue === value
        ) {
          return;
        }

        const input = Array.from(
          field.querySelectorAll("input")
        ).find(node => node.value === value);

        if (input) {
          input.checked = true;
          field.updateSelectedValue?.();
        }
      });
    }

    removeLoading(element) {
      if (!element) return;

      element.removeAttribute("data-media-loading");

      if (element.firstElementChild) {
        element.firstElementChild.style.opacity = 1;
      }
    }

    handleImageZoom(items, gallery) {
      const images = items.map(item => {
        const type = item.dataset.mediaType;

        if (type === "image") {
          const media = item.querySelector(
            ".m-product-media"
          )?.dataset;

          if (!media) return null;

          return {
            src: media.mediaSrc,
            width: parseInt(media.mediaWidth),
            height: parseInt(media.mediaHeight),
            alt: media.mediaAlt,
            id: item.dataset.index
          };
        }

        return {
          html: `<div class="pswp__${type}">${item.innerHTML}</div>`,
          type,
          id: item.dataset.index
        };
      }).filter(Boolean);

      if (gallery?.lightbox) {
        gallery.lightbox.destroy();
        gallery.handlePhotoswipe(images);
        gallery.initPhotoswipe();
      }
    }

    updateMedia() {
      if (!this.currentVariant || !this.media) return;

      let slides = [];
      let thumbnails = [];

      const reorder = (
        ids,
        elements,
        isThumbnail = false,
        setActive = false
      ) => {
        const result = [];

        ids.forEach((id, index) => {
          const element = elements.find(item => {
            return item.querySelector(
              "[data-media-id]"
            )?.dataset.mediaId === id;
          });

          if (element) {
            element.dataset.index = index;
            element.dataset.swiperSlideIndex = index;
            result.push(element);
          }
        });

        elements.forEach(element => {
          const id = element.querySelector(
            "[data-media-id]"
          )?.dataset.mediaId;

          if (
            !ids.includes(id) &&
            !result.includes(element)
          ) {
            element.dataset.index = result.length;
            element.dataset.swiperSlideIndex =
              result.length;

            result.push(element);
          }
        });

        if (setActive) {
          result.forEach(element => {
            const active =
              element.dataset.swiperSlideIndex === "0";

            element.classList.toggle(
              isThumbnail
                ? "swiper-slide-thumb-active"
                : "swiper-slide-active",
              active
            );
          });
        }

        return result;
      };

      const filterMedia = (elements, output) => {
        elements.forEach(element => {
          const id = element.querySelector(
            "[data-media-id]"
          )?.dataset.mediaId;

          const included =
            this.currentVariantMedia?.includes(id);

          const notImage = !element.classList.contains(
            "media-type-image"
          );

          if (
            included ||
            notImage ||
            !this.currentVariantMedia?.length
          ) {
            output.push(element);
          }
        });
      };

      if (
        this.variantGroupImages?.enable &&
        this.currentVariantMedia
      ) {
        if (
          this.media.mediaMode === "slider" &&
          this.slides
        ) {
          filterMedia(this.slides, slides);

          const ordered = reorder(
            this.currentVariantMedia,
            slides,
            false,
            true
          );

          this.media.slider.removeAllSlides();
          this.media.slider.appendSlide(ordered);

          this.media.slider.slideToLoop(
            this.layout === "layout-7" ? 1 : 0
          );

          this.media.handleSlideChange();

          if (this.slidesNav) {
            filterMedia(this.slidesNav, thumbnails);

            const orderedThumbs = reorder(
              this.currentVariantMedia,
              thumbnails,
              true,
              true
            );

            this.media.navSlider?.removeAllSlides();
            this.media.navSlider?.appendSlide(orderedThumbs);
            this.media.navSlider?.slideToLoop(0);
          }
        } else {
          const list = this.media.querySelector(
            ".m-product-media--list"
          );

          if (!list) return;

          filterMedia(this.mediaItems || [], slides);

          const ordered = reorder(
            this.currentVariantMedia,
            slides
          );

          list.innerHTML = "";

          ordered.forEach((element, index) => {
            if (this.layout === "layout-2") {
              element.classList.remove("m-col-span-2");

              if (index % 3 === 0) {
                element.classList.add("m-col-span-2");
              }
            }

            list.append(element);
          });
        }
      } else {
        const mediaSize = parseInt(
          this.media.dataset.mediaSize || "0",
          10
        );

        if (mediaSize > 0) {
          this.media.setActiveMedia(this.currentVariant);
        }
      }

      this.removeLoading(this.media);
      this.handleImageZoom(slides, this.media);
    }

    updateBrowserHistory() {
      if (
        !this.currentVariant ||
        this.dataset.updateUrl === "false"
      ) {
        return;
      }

      window.history.replaceState(
        {},
        "",
        `${this.productUrl}?variant=${this.currentVariant.id}`
      );
    }

    // FIX: Synchronize variant IDs across product forms.
    updateVariantInput() {
      if (!this.currentVariant) return;

      const variantId = String(this.currentVariant.id);

      const forms = new Set([
        ...document.querySelectorAll(
          `#product-form-${CSS.escape(this.sectionId)}, #product-form-installment`
        ),
        ...this.productWrapper.querySelectorAll(
          'form[action*="/cart/add"]'
        ),
        ...document.querySelectorAll(
          "form.sticky-atc-form"
        )
      ]);

      forms.forEach(form => {
        const input = form.querySelector('[name="id"]');

        if (!input) return;

        if (input.value !== variantId) {
          input.value = variantId;

          input.dispatchEvent(
            new Event("input", { bubbles: true })
          );

          input.dispatchEvent(
            new Event("change", { bubbles: true })
          );
        }

        if (form.matches(".sticky-atc-form")) {
          form.querySelectorAll("select").forEach(select => {
            const matchingOption = [
              ...select.options
            ].find(option => option.value === variantId);

            if (
              matchingOption &&
              select.value !== variantId
            ) {
              select.value = variantId;
            }
          });
        }
      });
    }

    updatePickupAvailability() {
      const pickup = this.productWrapper.querySelector(
        "pickup-availability"
      );

      if (
        pickup &&
        this.currentVariant?.available
      ) {
        pickup.fetchAvailability(
          this.currentVariant.id
        );
      } else if (pickup) {
        pickup.removeAttribute("available");
        pickup.innerHTML = "";
      }
    }

    removeErrorMessage() {
      const section = this.closest("section");
      const form = section?.querySelector("product-form");

      form?.handleErrorMessage();
    }

    updatePrice() {
      const container = this.productWrapper.querySelector(
        ".main-product__block-price"
      );

      if (!container || !this.currentVariant) return;

      const nodes = queryDomNodes({
        priceWrapper: ".m-price",
        salePrice: ".m-price-item--sale",
        compareAtPrice: [".m-price-item--regular"],
        unitPrice: ".m-price__unit",
        unitPriceWrapper: ".m-price__unit-wrapper",
        saleBadge: ".m-price__badge-sale",
        saleAmount: "[data-saved-price]"
      }, container);

      const {
        priceWrapper,
        salePrice,
        compareAtPrice,
        unitPrice,
        unitPriceWrapper,
        saleBadge,
        saleAmount
      } = nodes;

      if (!priceWrapper) return;

      const variant = this.currentVariant;
      const price = variant.price;
      const compare = variant.compare_at_price;
      const onSale = compare && compare > price;

      priceWrapper.classList.toggle(
        "m-price--on-sale",
        Boolean(onSale)
      );

      priceWrapper.classList.toggle(
        "m-price--sold-out",
        !variant.available
      );

      priceWrapper.classList.remove(
        "m:visibility-hidden"
      );

      const moneyFormat =
        window.MinimogSettings.money_format;

      if (salePrice) {
        salePrice.innerHTML = formatMoney(
          price,
          moneyFormat
        );
      }

      compareAtPrice?.forEach(element => {
        element.innerHTML = formatMoney(
          onSale ? compare : price,
          moneyFormat
        );
      });

      if (
        saleBadge &&
        onSale &&
        priceWrapper.dataset.saleBadgeType !== "text"
      ) {
        const amount =
          priceWrapper.dataset.saleBadgeType ===
          "fixed_amount"
            ? formatMoney(compare - price, moneyFormat)
            : Math.round(
                100 * (compare - price) / compare
              ) + "%";

        if (saleAmount) {
          saleAmount.innerHTML = amount;
        }
      }

      if (
        variant.unit_price_measurement &&
        unitPrice
      ) {
        unitPriceWrapper?.classList.remove("m:hidden");

        unitPrice.innerHTML =
          `<span>${formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>` +
          `${this._getBaseUnit()}</span>`;
      } else {
        unitPriceWrapper?.classList.add("m:hidden");
      }
    }

    _getBaseUnit = () => {
      const measurement =
        this.currentVariant.unit_price_measurement;

      if (!measurement) return "";

      return measurement.reference_value === 1
        ? measurement.reference_unit
        : `${measurement.reference_value}${measurement.reference_unit}`;
    };

    // FIX: Check that the button exists before accessing it.
    updateButton(disabled = true, label) {
      const forms = document.querySelectorAll(
        `.product-form-${this.sectionId}`
      );

      forms.forEach(form => {
        const button = form.querySelector(
          '[name="add"]'
        );

        if (!button) return;

        const dynamicCheckout = form.querySelector(
          ".m-product-dynamic-checkout"
        );

        const buttonText = button.querySelector(
          ".m-add-to-cart--text"
        );

        if (disabled) {
          button.setAttribute("disabled", "disabled");
          button.classList.add("disabled");

          dynamicCheckout?.classList.add("disabled");

          if (label && buttonText) {
            buttonText.textContent = label;
          }
        } else {
          button.removeAttribute("disabled");
          button.classList.remove("disabled");

          dynamicCheckout?.classList.remove("disabled");

          if (buttonText) {
            buttonText.textContent =
              window.MinimogStrings.addToCart;
          }
        }
      });
    }

    updateProductMeta() {
      if (!this.currentVariant) return;

      const variant = this.currentVariant;
      const strings = window.MinimogStrings;

      const availability = this.productWrapper.querySelector(
        this.selectors.productAvailability
      );

      const sku = this.productWrapper.querySelector(
        this.selectors.productSku
      );

      if (sku) {
        sku.textContent = variant.sku || "N/A";
      }

      if (availability) {
        availability.textContent = variant.available
          ? strings.inStock
          : strings.outOfStock;

        availability.classList.toggle(
          "m-product-availability--outofstock",
          !variant.available
        );
      }
    }

    setUnavailable() {
      const form = document.getElementById(
        `product-form-${this.sectionId}`
      );

      const button = form?.querySelector(
        '[name="add"]'
      );

      const buttonText = button?.querySelector(
        ".m-add-to-cart--text"
      );

      const price = this.productWrapper.querySelector(
        ".m-price"
      );

      if (buttonText) {
        buttonText.textContent =
          window.MinimogStrings.unavailable;
      }

      price?.classList.add("m:visibility-hidden");
    }

    hideSoldOutAndUnavailableOptions() {
      if (!this.currentVariant) return;

      const soldOutClass =
        "m-product-option--node__soldout";

      const unavailableClass =
        "m-product-option--node__unavailable";

      const nodes = this.domNodes.optionNodes;
      const variants = this.productData.variants;
      const options = this.productData.options;

      nodes.forEach(node => {
        const position = Number(
          node.dataset.optionPosition
        );

        const value = node.dataset.value;
        const isOption = node.tagName === "OPTION";

        let matching = [];

        if (position === options.length) {
          const selected = [
            ...this.currentVariant.options
          ];

          selected[position - 1] = value;

          matching.push(
            getVariantFromOptionArray(
              this.productData,
              selected
            )
          );
        } else {
          matching = variants.filter(variant => {
            return (
              variant.options[position - 1] === value &&
              variant.options[position - 2] ===
                this.currentVariant[
                  "option" + (position - 1)
                ]
            );
          });
        }

        matching = matching.filter(Boolean);

        if (matching.length) {
          node.classList.remove(unavailableClass);

          if (isOption) {
            node.removeAttribute("disabled");
          }

          node.classList.toggle(
            soldOutClass,
            matching.every(variant => !variant.available)
          );
        } else {
          node.classList.add(unavailableClass);

          if (isOption) {
            node.setAttribute("disabled", "true");
          }
        }
      });
    }

    getVariantData() {
      if (!this.variantData) {
        const element = this.querySelector(
          '#productVariants[type="application/json"]'
        );

        if (element) {
          try {
            this.variantData = JSON.parse(
              element.textContent
            );
          } catch (error) {
            console.error(error);
          }
        }
      }

      return this.variantData || [];
    }

    getVariantGroupImageData() {
      if (!this.variantGroupImages) {
        const element = this.querySelector(
          '#variantGroup[type="application/json"]'
        );

        if (element) {
          try {
            this.variantGroupImages = JSON.parse(
              element.textContent
            );
          } catch (error) {
            console.error(error);
          }
        }
      }

      return this.variantGroupImages || {};
    }

    initOptionSwatches() {
      const {
        _colorSwatches = [],
        _imageSwatches = []
      } = window.MinimogSettings;

      this.domNodes.optionNodes?.forEach(node => {
        const {
          optionType,
          optionPosition,
          value
        } = node.dataset;

        const key = value?.toLowerCase();

        const variant = this.variantData.find(
          item =>
            item[`option${optionPosition}`] === value
        );

        const image = variant?.featured_image?.src
          ? getSizedImageUrl(
              variant.featured_image.src,
              "100x"
            )
          : null;

        const imageSwatch = _imageSwatches.find(
          item => item.key === key
        );

        const colorSwatch = _colorSwatches.find(
          item => item.key === key
        );

        const label = node.querySelector("label");

        if (!label) return;

        if (image || imageSwatch?.value) {
          label.classList.add("has-bg-img");
        }

        label.classList.remove("option-loading");

        switch (optionType) {
          case "default":
          case "image":
            label.style.backgroundImage =
              `url(${imageSwatch?.value || image || ""})`;
            break;

          case "color":
            label.style.backgroundColor =
              colorSwatch?.value || key || "";

            if (imageSwatch?.value) {
              label.style.backgroundImage =
                `url(${imageSwatch.value})`;
            }
            break;
        }
      });
    }
  }

  customElements.define(
    "variant-picker",
    VariantPicker
  );
}

if (!customElements.get("variant-button")) {
  class VariantButton extends HTMLElement {
    constructor() {
      super();

      this.selectedSpan = this.querySelector(
        ".option-label--selected"
      );

      this.addEventListener(
        "change",
        () => this.updateSelectedValue()
      );
    }

    updateSelectedValue() {
      const selected = Array.from(
        this.querySelectorAll("input")
      ).find(input => input.checked);

      if (!selected) return;

      this.value = selected.value;

      this.setAttribute(
        "data-selected-value",
        this.value
      );

      if (this.selectedSpan) {
        this.selectedSpan.textContent = this.value;
        this.selectedSpan.classList.remove(
          "m:text-color-error"
        );
      }
    }
  }

  customElements.define(
    "variant-button",
    VariantButton
  );

  if (!customElements.get("variant-select")) {
    class VariantSelect extends VariantButton {
      updateSelectedValue() {
        const select = this.querySelector("select");

        if (!select) return;

        this.value = select.value;

        this.setAttribute(
          "data-selected-value",
          this.value
        );

        if (this.selectedSpan) {
          this.selectedSpan.textContent = this.value;
          this.selectedSpan.classList.remove(
            "m:text-color-error"
          );
        }
      }
    }

    customElements.define(
      "variant-select",
      VariantSelect
    );
  }

  if (!customElements.get("variant-image")) {
    class VariantImage extends VariantButton {}

    customElements.define(
      "variant-image",
      VariantImage
    );
  }

  if (!customElements.get("variant-color")) {
    class VariantColor extends VariantButton {}

    customElements.define(
      "variant-color",
      VariantColor
    );
  }
}
